import type { Tone } from '@/components/ui/StatusBadge';

/**
 * Central Hebrew dictionary for backend enums + a status→tone map. Stops raw
 * English enum strings from leaking LTR into the RTL UI, and keeps colour
 * coding consistent across screens.
 */

const ROLE: Record<string, string> = {
  owner: 'בעלים', admin: 'מנהל', supervisor: 'מפקח', qa: 'בקרת איכות',
  agent: 'נציג', viewer: 'צופה',
};

const USER_STATUS: Record<string, string> = {
  active: 'פעיל', suspended: 'מושעה', invited: 'הוזמן', disabled: 'מושבת',
};

const CALL_STATUS: Record<string, string> = {
  completed: 'הושלמה', answered: 'נענתה', missed: 'לא נענתה', failed: 'נכשלה',
  busy: 'תפוס', 'no-answer': 'אין מענה', abandoned: 'ננטשה', in_progress: 'מתבצעת',
  ringing: 'מצלצלת', queued: 'בתור',
};

const QUEUE_STRATEGY: Record<string, string> = {
  'round-robin': 'סבב', ringall: 'צלצול לכולם', leastrecent: 'הכי פחות עדכני',
  fewestcalls: 'הכי מעט שיחות', random: 'אקראי', linear: 'לינארי',
};

const DIALER_MODE: Record<string, string> = {
  preview: 'תצוגה מקדימה', progressive: 'מתקדם', predictive: 'חזוי', power: 'עוצמה',
};

const CHANNEL: Record<string, string> = { sms: 'SMS', whatsapp: 'וואטסאפ', voice: 'קולי' };

const DID_TYPE: Record<string, string> = {
  landline: 'קווי', virtual: 'וירטואלי', mobile: 'נייד', tollfree: 'חינם',
};

const SENTIMENT: Record<string, string> = {
  positive: 'חיובי', negative: 'שלילי', neutral: 'נייטרלי',
};

const TENANT_STATUS: Record<string, string> = {
  active: 'פעיל', trial: 'ניסיון', suspended: 'מושעה', deleted: 'נמחק',
};

const DICTS: Record<string, Record<string, string>> = {
  role: ROLE, user_status: USER_STATUS, call_status: CALL_STATUS,
  strategy: QUEUE_STRATEGY, dialer_mode: DIALER_MODE, channel: CHANNEL,
  did_type: DID_TYPE, sentiment: SENTIMENT, tenant_status: TENANT_STATUS,
};

/** Translate an enum value in a given domain to Hebrew, falling back to the raw value. */
export function labelFor(domain: keyof typeof DICTS | string, value?: string | null): string {
  if (value == null) return '—';
  return DICTS[domain]?.[value] ?? value;
}

/** Map a status-ish value to a StatusBadge tone. */
export function toneFor(value?: string | null): Tone {
  switch (value) {
    case 'active': case 'completed': case 'answered': case 'healthy': case 'positive': case 'paid':
      return 'ok';
    case 'trial': case 'invited': case 'queued': case 'ringing': case 'in_progress': case 'pending': case 'neutral':
      return 'info';
    case 'busy': case 'no-answer': case 'abandoned': case 'warn':
      return 'warn';
    case 'suspended': case 'failed': case 'missed': case 'deleted': case 'disabled': case 'negative': case 'overdue':
      return 'danger';
    default:
      return 'neutral';
  }
}
